import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Category, loadCategoriesFromServer } from '../../utils/categories';
import { Package } from '../../types/Package';
import { loadServicesFromServer } from '../../utils/ServiceAPI';
import { generatePackageFinderShareContent } from '../../utils/packageFinderShare';
import { generateSharingUrl, copyToClipboard, PLATFORMS, SocialPlatform } from '../../utils/sharingUrls';
import './PackageFinderWizard.css';

interface SharePackagesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SharePackagesModal: React.FC<SharePackagesModalProps> = ({
  isOpen,
  onClose
}) => {
  const [currentStep, setCurrentStep] = useState<'category' | 'share'>('category');
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [packages, setPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(false);
  const [shareContent, setShareContent] = useState<any>(null);
  const [copySuccess, setCopySuccess] = useState(false);

  // Load categories and packages when modal opens
  useEffect(() => {
    if (isOpen) {
      const loadData = async () => {
        setLoading(true);
        try {
          const [loadedCategories, loadedPackages] = await Promise.all([
            loadCategoriesFromServer(),
            loadServicesFromServer()
          ]);
          setCategories(loadedCategories);
          setPackages(loadedPackages);
        } catch (error) {
          console.error('Error loading categories or packages:', error);
        } finally {
          setLoading(false);
        }
      };
      loadData();
    }
  }, [isOpen]);

  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      setCurrentStep('category');
      setSelectedCategory(null);
      setShareContent(null);
    }
  }, [isOpen]);

  // Get categories that have available packages
  const getCategoriesWithPackages = (): Category[] => {
    const availablePackages = packages.filter(pkg => pkg.available !== false);
    
    return categories.filter(category => {
      return availablePackages.some(pkg => {
        if (pkg.categories && pkg.categories.length > 0) {
          return pkg.categories.some((catSlug: string) => 
            catSlug.toLowerCase() === category.slug.toLowerCase()
          );
        }
        if (pkg.category) {
          return pkg.category.toLowerCase() === category.name.toLowerCase() ||
                 pkg.category.toLowerCase() === category.slug.toLowerCase();
        }
        return false;
      });
    });
  };

  const handleCategorySelect = (category: Category) => {
    setSelectedCategory(category);
    
    // Generate share content for the selected category
    const content = generatePackageFinderShareContent({ 
      open: true, 
      category: category.name 
    });
    
    setShareContent(content);
    setCurrentStep('share');
  };

  const handleShare = (platform: SocialPlatform) => {
    if (!shareContent) return;

    // Handle Instagram - use Web Share API if available
    if (platform === 'instagram') {
      const isWebShareAvailable = typeof navigator !== 'undefined' && 'share' in navigator;
      if (isWebShareAvailable) {
        navigator.share({
          title: shareContent.title,
          text: shareContent.description,
          url: shareContent.url
        }).catch(() => {
          // Fallback to copying link
          handleCopyLink();
        });
        return;
      } else {
        // Fallback to copying link
        handleCopyLink();
        return;
      }
    }

    // Handle WhatsApp - special handling
    if (platform === 'whatsapp') {
      const { title, description, url, hashtags } = shareContent;
      const hashtagString = hashtags ? hashtags.map((tag: string) => `#${tag}`).join(' ') : '';
      const message = `${url}\n\n${title}\n\n${description}${hashtagString ? '\n\n' + hashtagString : ''}`;
      const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');
      return;
    }

    // Generate and open share URL for other platforms
    const shareUrl = generateSharingUrl(platform, shareContent);
    
    if (platform === 'email') {
      window.location.href = shareUrl;
    } else {
      window.open(shareUrl, '_blank', 'width=600,height=400');
    }
  };

  const handleCopyLink = async () => {
    if (!shareContent) return;
    const success = await copyToClipboard(shareContent.url);
    if (success) {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const getPlatformConfig = (platformId: SocialPlatform) => {
    return PLATFORMS.find(p => p.id === platformId);
  };


  if (!isOpen) return null;

  const categoriesWithPackages = getCategoriesWithPackages();

  const modalContent = (
    <>
      <div 
        className="package-finder-overlay"
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 100004,
          backdropFilter: 'blur(4px)'
        }}
      >
        <div 
          className="package-finder-modal"
          onClick={(e) => e.stopPropagation()}
          style={{
            background: 'white',
            borderRadius: '16px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
            zIndex: 100005
          }}
        >
          {/* Header */}
          <div style={{
            padding: '24px',
            borderBottom: '2px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)'
          }}>
            <div>
              <h2 style={{ margin: 0, fontSize: '1.5rem', color: '#111827', fontFamily: 'var(--font-body, Inter, sans-serif)' }}>
                {selectedCategory ? `Share ${selectedCategory.name} Packages` : 'Share Packages'}
              </h2>
              <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '0.875rem' }}>
                {selectedCategory 
                  ? 'Choose how you\'d like to share' 
                  : 'Select a category to share'}
              </p>
            </div>
            <button
              onClick={selectedCategory ? () => {
                setSelectedCategory(null);
                setShareContent(null);
                setCurrentStep('category');
              } : onClose}
              style={{
                background: 'rgba(139, 69, 19, 0.1)',
                color: 'var(--color-primary, #8B4513)',
                border: '2px solid rgba(139, 69, 19, 0.2)',
                borderRadius: '8px',
                width: '40px',
                height: '40px',
                cursor: 'pointer',
                fontSize: '1.1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--color-primary, #8B4513)';
                e.currentTarget.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(139, 69, 19, 0.1)';
                e.currentTarget.style.color = 'var(--color-primary, #8B4513)';
              }}
            >
              <i className={selectedCategory ? 'fas fa-arrow-left' : 'fas fa-times'}></i>
            </button>
          </div>

          {/* Content */}
          <div style={{
            padding: '24px',
            overflowY: 'auto',
            flex: 1
          }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', color: '#8B4513' }}></i>
                <p style={{ marginTop: '1rem', color: '#6b7280' }}>Loading categories...</p>
              </div>
            ) : !selectedCategory ? (
              <div>
                <p style={{ marginBottom: '1.5rem', color: '#374151', fontSize: '0.9375rem' }}>
                  Choose a photography category to share with others:
                </p>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                  gap: '12px'
                }}>
                  {categoriesWithPackages.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => handleCategorySelect(category)}
                      style={{
                        padding: '16px',
                        borderRadius: '12px',
                        border: '2px solid #e5e7eb',
                        background: 'white',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = category.color || '#8B4513';
                        e.currentTarget.style.background = `${category.color || '#8B4513'}15`;
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#e5e7eb';
                        e.currentTarget.style.background = 'white';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <div style={{
                        fontSize: '2rem',
                        marginBottom: '4px'
                      }}>
                        {'📸'}
                      </div>
                      <span style={{
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: '#111827'
                      }}>
                        {category.name}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div>
                <div style={{
                  background: '#f9fafb',
                  borderRadius: '12px',
                  padding: '20px',
                  marginBottom: '1.5rem',
                  border: '1px solid #e5e7eb'
                }}>
                  <h3 style={{ margin: '0 0 12px 0', fontSize: '1.125rem', color: '#111827' }}>
                    Share {selectedCategory.name} Packages
                  </h3>
                  <p style={{ margin: 0, color: '#6b7280', fontSize: '0.875rem' }}>
                    Share the Package Finder tool with others so they can discover {selectedCategory.name.toLowerCase()} photography packages tailored to their needs.
                  </p>
                </div>
                
                {shareContent && (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                    gap: '12px'
                  }}>
                    {['whatsapp', 'twitter', 'facebook', 'instagram', 'email'].map((platformId) => {
                      const platform = getPlatformConfig(platformId as SocialPlatform);
                      if (!platform) return null;

                      return (
                        <button
                          key={platform.id}
                          onClick={() => handleShare(platform.id)}
                          style={{
                            padding: '14px 20px',
                            background: platform.color,
                            color: 'white',
                            border: 'none',
                            borderRadius: '12px',
                            fontSize: '0.9375rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                          }}
                          onMouseEnter={(e) => {
                            const hoverColor = platform.color === '#25D366' ? '#20ba5a' :
                                              platform.color === '#1DA1F2' ? '#1a8cd8' :
                                              platform.color === '#1877F2' ? '#166fe5' :
                                              platform.color === '#E4405F' ? '#d32e4a' :
                                              'var(--color-secondary, #a0522d)';
                            e.currentTarget.style.background = hoverColor;
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = `0 4px 12px ${platform.color}40`;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = platform.color;
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        >
                          <i className={platform.icon}></i>
                          <span>{platform.name}</span>
                        </button>
                      );
                    })}
                    
                    <button
                      onClick={handleCopyLink}
                      style={{
                        padding: '14px 20px',
                        background: copySuccess ? '#10b981' : '#6b7280',
                        color: 'white',
                        border: 'none',
                        borderRadius: '12px',
                        fontSize: '0.9375rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                      onMouseEnter={(e) => {
                        if (!copySuccess) {
                          e.currentTarget.style.background = '#4b5563';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(107, 114, 128, 0.3)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!copySuccess) {
                          e.currentTarget.style.background = '#6b7280';
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }
                      }}
                    >
                      <i className={copySuccess ? 'fas fa-check' : 'fas fa-copy'}></i>
                      <span>{copySuccess ? 'Copied!' : 'Copy Link'}</span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(modalContent, document.body);
};

export default SharePackagesModal;

